// patients-view.js — CLIENT-SIDE shaping, raw FHIR -> the buildPatientsView
// row DTO (server/opd/view-adapter.mjs:2647), computed from a Patient search
// with _revinclude=Encounter:subject.
//
// NAMES-TO-ALL is free under RLS: the current opd-doctor-rowlevel AccessPolicy
// gives Patient no criteria (demographics open to everyone — REDESIGN_ONLINE_PRIMARY.md
// P0), so an unscoped Patient search already returns every patient; only the
// _revinclude'd Encounters are RLS-filtered to what this user may actually read.
// That's what drives `relationship` below.
//
// ACCESS TIER (restored 2026-08-04, DEFERRED §59). The readable Encounters are
// NOT all "my department": the doctor policy's Encounter arms are
// service-provider=%department OR practitioner=%profile OR _compartment=%profile
// (what the access-grant Bot stamps) OR class=EMER. So "I can read this
// encounter" and "this encounter is my department's" are different facts, and
// flattening them (the old `encounters.length > 0 ? 'department' : 'none'`) made
// the green "Access" chip dead. `relationship` is now computed against the
// caller's OWN department Organization id:
//   • any readable encounter served by my department        → 'department'
//   • else any readable encounter served by ANOTHER org      → 'access'
//     (I reach this patient by grant / as the treating doctor / via the shared
//      emergency surface — never through my department)
//   • no readable encounters, or the caller has no department → 'none'
// An encounter with NO serviceProvider establishes neither tier — it is not my
// department's and it is not evidence of cross-department reach. That matches
// the server query the 'access' scope runs (`service-provider:not=Organization/X`
// does not match a null serviceProvider — Medplum builds it as NOT(col = x),
// which is NULL, not TRUE, for a null column: medplum-link/packages/server/src/
// fhir/search.ts buildReferenceSearchFilter), so the chip and the tab agree.
//
// `chartGated` (the old Lock-1 chart-read-gate's per-row re-check) is dropped,
// not ported: the redesign's replacing invariant is "authorized reads only,
// no app-side re-check" (AGENTS.md §2) — whatever this search returns IS
// authorized, so there's nothing left to gate here.
import { consultUrl } from '../urls.js';
import { ageFromBirthDate, nameOf, initials } from '../fhir-person.js';
import { formatInClinicTz } from '../clinic-time.js';

const OPD_STATE = 'https://healther.io/opd/state';
const MRN_SYS = 'https://healther.io/opd/mrn';

const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
// clinic-tz via clinic-time.js, 2026-08-05 W2.1 — was device-tz toLocaleDateString.
const fmtDate = (iso) => formatInClinicTz(iso, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

// Groups a Patient?_revinclude=Encounter:subject bundle into { patients,
// encountersByPatient } — patients keyed by 'Patient/<id>', encounters
// grouped under the same key and time-sorted newest-first.
export function groupPatientBundle(bundle) {
  const patients = []; const encountersByPatient = {};
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    if (r.resourceType === 'Patient') patients.push(r);
    else if (r.resourceType === 'Encounter') {
      const ref = r.subject && r.subject.reference;
      if (ref) (encountersByPatient[ref] = encountersByPatient[ref] || []).push(r);
    }
  }
  for (const encs of Object.values(encountersByPatient)) {
    encs.sort((a, b) => String((b.period && b.period.start) || '').localeCompare(String((a.period && a.period.start) || '')));
  }
  return { patients, encountersByPatient };
}

/**
 * Which access tier does this row sit in for the logged-in user? See the
 * ACCESS TIER note at the top of this file for the rule and why it is computed
 * from serviceProvider rather than from "did any encounter come back".
 *
 * @param {Array}       encounters  the patient's RLS-readable Encounters.
 * @param {string|null} deptOrgId   the caller's OWN department Organization id
 *   (bare uuid). null when the caller has no department (owner / admin /
 *   front desk) — the tier is undefined for them, so every row is 'none'.
 * @returns {'department'|'access'|'none'}
 */
export function classifyRelationship(encounters, deptOrgId) {
  if (!deptOrgId) return 'none';
  const deptRef = 'Organization/' + deptOrgId;
  let crossDepartment = false;
  for (const e of encounters || []) {
    const sp = (e && e.serviceProvider && e.serviceProvider.reference) || null;
    if (!sp) continue;
    if (sp === deptRef) return 'department';
    crossDepartment = true;
  }
  return crossDepartment ? 'access' : 'none';
}

export function shapePatientRow(patient, encounters, deptOrgId = null) {
  const name = nameOf(patient);
  const mrn = (patient.identifier || []).find((i) => i.system === MRN_SYS);
  const phone = (patient.telecom || []).find((t) => t.system === 'phone');
  const last = (encounters || [])[0] || null;
  const lastStateExt = last && ext(last, OPD_STATE);
  const lastState = lastStateExt ? lastStateExt.valueString : null;
  return {
    id: patient.id,
    medplumId: patient.id,
    displayName: name,
    initials: initials(name) || '?',
    age: ageFromBirthDate(patient.birthDate),
    sex: patient.gender ? patient.gender[0].toUpperCase() : '',
    phone: phone ? phone.value : '',
    language: null,
    mrn: mrn ? mrn.value : '',
    encounterCount: (encounters || []).length,
    lastEncounterId: last ? last.id : null,
    lastEncounterState: lastState,
    // The FHIR extension already stores the view-level state string (see
    // shapeQueueRow) — no separate internal->view mapping needed client-side.
    lastEncounterStateView: lastState,
    lastEncounterAt: last && last.period ? last.period.start : null,
    lastEncounterAtLabel: last && last.period ? fmtDate(last.period.start) : '',
    lastEncounterDoctor: last && last.participant && last.participant[0] && last.participant[0].individual ? last.participant[0].individual.display || '' : '',
    lastEncounterDepartment: last && last.serviceProvider ? last.serviceProvider.display || '' : '',
    consultHref: last ? consultUrl(last.id, patient.id) : null,
    relationship: classifyRelationship(encounters, deptOrgId),
    accessDetail: null,
    chartGated: false,
  };
}
